import { Link } from 'react-router-dom'
import { Calendar, ArrowRight, ShieldCheck, Orbit, UserCheck, Phone, MapPin } from 'lucide-react'
import { scrapedData } from '../data'
import SEO from '../components/SEO'
import { imageUrl, srcSet } from '../lib/images'

export default function About() {
  const { therapists } = scrapedData

  // 3.3 De tre fremhævede nøgleord med matchende ikoner
  const corePrinciples = [
    {
      keyword: "Faglighed",
      icon: <ShieldCheck className="w-8 h-8 text-[#eebb53]" />,
      desc: "Vores behandlinger bygger på solid sundhedsfaglig viden, mange års praktisk erfaring og løbende efteruddannelse."
    },
    {
      keyword: "Helhed",
      icon: <Orbit className="w-8 h-8 text-[#eebb53]" />,
      desc: "Vi ser kroppen og mennesket som en uadskillelig helhed, hvor fysiske spændinger, vaner og nervesystem påvirker hinanden."
    },
    {
      keyword: "Individuel behandling",
      icon: <UserCheck className="w-8 h-8 text-[#eebb53]" />,
      desc: "Ingen to kroppe eller symptomer er ens. Vi skræddersyr altid behandlingen, teknikkerne og forløbet specifikt til dig."
    }
  ]

  // Rækkefølge af behandlere ifølge punkt 3.2: Inge-Marie, Søren, Johanne
  const orderedTherapists = [
    therapists.find((t) => t.slug === 'inge-marie-roerbaek'),
    therapists.find((t) => t.slug === 'soeren-roerbaek'),
    therapists.find((t) => t.slug === 'johanne')
  ].filter(Boolean)

  return (
    <div className="space-y-20 pb-20">
      <SEO
        title="Om Rørbækklinikken | Mød vores behandlere i Viborg"
        description="Rørbækklinikken blev etableret i 1997. Mød Inge-Marie Rørbæk, Søren Rørbæk og Johanne i klinikken på Gravene 28B i Viborg."
      />

      {/* Page Hero Header */}
      <section className="bg-[#F5F0E6]/50 py-16 sm:py-20 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
            Hvem er vi
          </div>
          {/* Overordnet overskrift */}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E352F]">
            Om Rørbækklinikken
          </h1>
        </div>
      </section>

      {/* 3.1 Klinikkens historie */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-[#E6DFD3] p-8 sm:p-12 lg:p-16 shadow-xs space-y-6">
          <div className="inline-flex items-center gap-2 text-xs font-bold text-[#eebb53] uppercase tracking-wider">
            Vores historie og rødder
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1E352F]">
            Klinikkens historie
          </h2>
          
          <div className="space-y-4 text-base sm:text-lg text-[#1E352F]/85 leading-relaxed">
            <p>
              Rørbækklinikken blev etableret i 1997 med en vision om at skabe en klinik med afsæt i det etablerede sundhedssystem – kombineret med komplementære behandlingsformer.
            </p>
            <p>
              Grundlaget var Inge-Maries jordemoderfaglige baggrund og ønsket om at se mennesket og kroppen som en helhed.
            </p>
            <p>
              I dag består Rørbækklinikken af Søren, Johanne og Inge-Marie. Vi arbejder ud fra de samme grundlæggende principper, men har hver vores faglige baggrund og specialer.
            </p>
          </div>
        </div>
      </section>

      {/* 3.2 Sektion: Mød os */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E352F]/5 text-[#1E352F] text-xs font-semibold tracking-wider uppercase">
            Behandlerne
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F]">
            Mød os
          </h2>
          <p className="text-base text-[#1E352F]/75 max-w-2xl mx-auto leading-relaxed">
            Her kan du læse en kort introduktion til os alle tre. Klik på Læs mere for at se uddybende oplysninger om den enkelte behandler.
          </p>
        </div>

        {/* De tre behandlere præsenteret hver for sig */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {orderedTherapists.map((therapist) => (
            <div
              key={therapist.slug}
              className="bg-white rounded-3xl border border-[#E6DFD3] overflow-hidden flex flex-col justify-between shadow-xs hover:shadow-md transition-shadow"
            >
              <div>
                {/* Portrætbillede */}
                <div className="h-72 sm:h-80 w-full overflow-hidden bg-[#F5F0E6] relative">
                  <img
                    src={imageUrl(therapist.image, 'width=600,quality=85,format=auto')}
                    alt={therapist.name}
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>

                {/* Indhold */}
                <div className="p-6 sm:p-8 space-y-4">
                  <div className="space-y-1">
                    <h3 className="font-serif text-2xl font-bold text-[#1E352F]">
                      {therapist.name}
                    </h3>
                    <p className="text-xs sm:text-sm font-semibold text-[#eebb53] tracking-wide">
                      {therapist.title}
                    </p>
                  </div>

                  <p className="text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
                    {therapist.aboutDescription}
                  </p>
                </div>
              </div>

              {/* Læs mere-knap */}
              <div className="p-6 sm:p-8 pt-0">
                <Link
                  to={`/om-os/${therapist.slug}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#1E352F] hover:bg-[#2D4A43] text-[#F5F0E6] text-sm font-bold py-3 px-6 rounded-full transition-colors text-center shadow-xs"
                >
                  Læs mere
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 3.3 Sektion: Sådan arbejder vi */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#FDFBF7] rounded-3xl border border-[#E6DFD3] p-8 sm:p-12 lg:p-16 space-y-12">
          
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-[#eebb53] uppercase tracking-wider">
              Sådan arbejder vi
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1E352F]">
              Tre behandlere – én fælles tilgang
            </h2>
            <div className="space-y-3 text-base sm:text-lg text-[#1E352F]/85 leading-relaxed pt-2">
              <p>
                Selvom vi har forskellige faglige baggrunde og specialer, arbejder vi ud fra de samme grundlæggende principper.
              </p>
              <p>
                Vi ser kroppen som en helhed og tager udgangspunkt i det enkelte menneske. Derfor kan en behandling bestå af forskellige teknikker, og vi kombinerer behandlingsformer, når det er relevant.
              </p>
            </div>
          </div>

          {/* Tre fremhævede nøgleord som tre tydeligt adskilte elementer */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            {corePrinciples.map((principle, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl border border-[#E6DFD3] p-6 sm:p-8 space-y-4 shadow-xs flex flex-col items-start"
              >
                <div className="p-3 rounded-xl bg-[#F5F0E6]">
                  {principle.icon}
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#1E352F]">
                  {principle.keyword}
                </h3>
                <p className="text-sm text-[#1E352F]/75 leading-relaxed">
                  {principle.desc}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 3.4 Afsluttende sektion: Klinikken */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-[#E6DFD3] overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-center">
            
            {/* Facadebillede */}
            <div className="lg:col-span-6 h-72 sm:h-96 lg:h-full min-h-[340px] bg-[#F5F0E6] relative overflow-hidden">
              <img
                src={imageUrl("https://assets.auxodev.dk/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png", "width=1200,quality=90,format=auto")}
                srcSet={srcSet("https://assets.auxodev.dk/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/ra-rba-kklinikken-forside.png", [600, 1000, 1400])}
                sizes="(max-width: 1024px) 100vw, 50vw"
                alt="Rørbækklinikken facade på Gravene 28B i Viborg"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            {/* Klinikpræsentation tekst */}
            <div className="lg:col-span-6 p-8 sm:p-12 lg:p-14 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-[#eebb53] uppercase tracking-wider">
                Fysiske rammer
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1E352F]">
                Velkommen i Rørbækklinikken
              </h2>
              <p className="text-base sm:text-lg text-[#1E352F]/85 leading-relaxed">
                Rørbækklinikken ligger centralt i Viborg på Gravene 28B. Her behandler vi både børn og voksne i rolige og trygge omgivelser.
              </p>

              {/* Adresse og telefonnummer */}
              <div className="space-y-3 py-2 border-y border-[#E6DFD3] text-sm sm:text-base text-[#1E352F]">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span className="font-semibold">Gravene 28B · 8800 Viborg</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-[#eebb53] shrink-0" />
                  <span>
                    Telefon:{' '}
                    <a href="tel:86639393" className="font-bold hover:underline">
                      86 63 93 93
                    </a>
                  </span>
                </div>
              </div>

              {/* Knapper */}
              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <a
                  href="https://roerbaekklinikken.planway.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-7 py-3.5 rounded-full text-sm font-bold uppercase tracking-wider transition-colors shadow-sm"
                >
                  <Calendar className="w-4 h-4" />
                  BOOK TID
                </a>
                <Link
                  to="/klinikken"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1E352F] hover:bg-[#2D4A43] text-white px-7 py-3.5 rounded-full text-sm font-bold uppercase tracking-wider transition-colors shadow-sm"
                >
                  SE KLINIKKEN
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3.5 Afsluttende tekst (Værdierklæring) */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="p-8 sm:p-10 rounded-2xl bg-[#F5F0E6] border border-[#E6DFD3] shadow-xs">
          <p className="font-serif text-xl sm:text-2xl lg:text-3xl font-bold text-[#1E352F] leading-snug">
            Ingen mennesker er ens – og derfor skal behandling heller ikke være det.
          </p>
        </div>
      </section>

    </div>
  )
}
